import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from './components/ui/toaster';
import { CartProvider } from './context/CartContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { initObservability } from './lib/observability';

const lazyRoute = (loader, key) => lazy(() => loader()
  .then((module) => {
    try { sessionStorage.removeItem(`route-retry:${key}`); } catch { /* ignore */ }
    return module;
  })
  .catch((error) => {
    const retryKey = `route-retry:${key}`;
    let alreadyRetried = false;
    try {
      alreadyRetried = sessionStorage.getItem(retryKey) === '1';
      if (!alreadyRetried) sessionStorage.setItem(retryKey, '1');
    } catch { /* ignore */ }
    if (!alreadyRetried) {
      window.location.reload();
      return new Promise(() => {});
    }
    try { sessionStorage.removeItem(retryKey); } catch { /* ignore */ }
    throw error;
  }));
const lazyNamedRoute = (loader, name, key) => lazyRoute(() => loader().then((module) => ({ default: module[name] })), key);

const Home = lazyRoute(() => import('./pages/Home'), 'home');
const Landing = lazyRoute(() => import('./pages/Landing'), 'landing');
const Checkout = lazyRoute(() => import('./pages/Checkout'), 'checkout');
const OrderTracking = lazyRoute(() => import('./pages/OrderTracking'), 'order-tracking');
const AdminLayout = lazyRoute(() => import('./pages/admin/AdminLayout'), 'admin-layout');
const Login = lazyRoute(() => import('./pages/admin/Login'), 'login');
const Dashboard = lazyRoute(() => import('./pages/admin/Dashboard'), 'dashboard');
const Mesas = lazyRoute(() => import('./pages/admin/Mesas'), 'mesas');
const MesaDetail = lazyRoute(() => import('./pages/admin/MesaDetail'), 'mesa-detail');
const Comandas = lazyRoute(() => import('./pages/admin/Comandas'), 'comandas');
const ComandaDetail = lazyRoute(() => import('./pages/admin/ComandaDetail'), 'comanda-detail');
const Balcao = lazyRoute(() => import('./pages/admin/Balcao'), 'balcao');
const Pedidos = lazyRoute(() => import('./pages/admin/Pedidos'), 'pedidos');
const Produtos = lazyRoute(() => import('./pages/admin/Produtos'), 'produtos');
const ProdutoForm = lazyRoute(() => import('./pages/admin/ProdutoForm'), 'produto-form');
const Categorias = lazyRoute(() => import('./pages/admin/Categorias'), 'categorias');
const Caixa = lazyRoute(() => import('./pages/admin/Caixa'), 'caixa');
const MovimentacoesCaixa = lazyRoute(() => import('./pages/admin/MovimentacoesCaixa'), 'movimentacoes-caixa');
const Relatorios = lazyRoute(() => import('./pages/admin/Relatorios'), 'relatorios');
const Usuarios = lazyRoute(() => import('./pages/admin/Usuarios'), 'usuarios');
const VisaoGeral = lazyRoute(() => import('./pages/admin/VisaoGeral'), 'visao-geral');
const ConfiguracoesGerais = lazyRoute(() => import('./pages/admin/ConfiguracoesGerais'), 'configuracoes-gerais');
const Servicos = lazyRoute(() => import('./pages/admin/Servicos'), 'servicos');
const SetoresImpressao = lazyRoute(() => import('./pages/admin/SetoresImpressao'), 'setores-impressao');
const Preparo = lazyRoute(() => import('./pages/admin/Preparo'), 'preparo');
const Auditoria = lazyRoute(() => import('./pages/admin/Auditoria'), 'auditoria');
const DeliveryKanban = lazyRoute(() => import('./pages/admin/DeliveryKanban'), 'delivery');
const CardapioConfig = lazyRoute(() => import('./pages/admin/CardapioConfig'), 'cardapio-config');
const QRCodePage = lazyRoute(() => import('./pages/admin/QRCodePage'), 'qrcode');
const Promocoes = lazyRoute(() => import('./pages/admin/Promocoes'), 'promocoes');
const Estoque = lazyRoute(() => import('./pages/admin/Estoque'), 'estoque');
const FinanceiroFormasPagamento = lazyRoute(() => import('./pages/admin/FinanceiroFormasPagamento'), 'formas-pagamento');
const FinanceiroContas = lazyRoute(() => import('./pages/admin/FinanceiroContas'), 'financeiro-contas');
const FinanceiroExtrato = lazyRoute(() => import('./pages/admin/FinanceiroExtrato'), 'financeiro-extrato');
const Clientes = lazyRoute(() => import('./pages/admin/Clientes'), 'clientes');
const Perfil = lazyRoute(() => import('./pages/admin/Perfil'), 'perfil');
const ConfiguracoesBalanca = lazyRoute(() => import('./pages/admin/ConfiguracoesBalanca'), 'configuracoes-balanca');
const ConfiguracoesImpressao = lazyRoute(() => import('./pages/admin/ConfiguracoesImpressao'), 'configuracoes-impressao');
const ConfiguracoesHardware = lazyRoute(() => import('./pages/admin/ConfiguracoesHardware'), 'configuracoes-hardware');
const LocalizacaoEnderecos = lazyNamedRoute(() => import('./pages/admin/Placeholders'), 'LocalizacaoEnderecos', 'localizacao-enderecos');
const Avaliacoes = lazyNamedRoute(() => import('./pages/admin/Placeholders'), 'Avaliacoes', 'avaliacoes');
const Sobre = lazyNamedRoute(() => import('./pages/admin/Placeholders'), 'Sobre', 'sobre');
const Termos = lazyNamedRoute(() => import('./pages/LegalPage'), 'Termos', 'termos');
const Privacidade = lazyNamedRoute(() => import('./pages/LegalPage'), 'Privacidade', 'privacidade');
const TermosPublic = lazyNamedRoute(() => import('./pages/LegalPage'), 'TermosPublic', 'termos-public');
const PrivacidadePublic = lazyNamedRoute(() => import('./pages/LegalPage'), 'PrivacidadePublic', 'privacidade-public');
const SuperAdminAudit = lazyRoute(() => import('./pages/admin/SuperAdminAudit'), 'superadmin-audit');
const SuperAdminDomains = lazyRoute(() => import('./pages/admin/SuperAdminDomains'), 'superadmin-domains');
const SuperAdminLayout = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminLayout'), 'superadmin-layout');
const SuperAdminDashboardV2 = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminDashboard'), 'superadmin-dashboard');
const SuperAdminLojas = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminLojas'), 'superadmin-lojas');
const SuperAdminPlanos = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminPlanos'), 'superadmin-planos');
const SuperAdminFinanceiro = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminFinanceiro'), 'superadmin-financeiro');
const SuperAdminUsuarios = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminUsuarios'), 'superadmin-usuarios');
const SuperAdminSlugsV2 = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminSlugs'), 'superadmin-slugs');
const SuperAdminRetencao = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminRetencao'), 'superadmin-retencao');
const SuperAdminSaude = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminSaude'), 'superadmin-saude');
const SuperAdminAgentes = lazyRoute(() => import('./pages/admin/superadmin/SuperAdminAgentes'), 'superadmin-agentes');

const RouteFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center" role="status" aria-label="Carregando tela">
    <span className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-[#2563eb] dark:border-zinc-700 dark:border-t-[#60a5fa]" />
  </div>
);

initObservability();

// No subdomínio app.*, a raiz leva direto ao login. No domínio raiz, mostra Landing.
function isAppDomain() {
  try {
    const host = window.location.hostname.toLowerCase();
    return host.startsWith('app.');
  } catch { return false; }
}
function AppRoot() {
  if (isAppDomain()) return <Navigate to="/login" replace />;
  return <Landing />;
}

// Redireciona acesso pelo domínio Railway para o domínio oficial customizado.
const RAILWAY_DOMAIN = 'damatt-systems-production.up.railway.app';
function CanonicalDomainRedirect({ children }) {
  try {
    const host = window.location.hostname.toLowerCase();
    if (host === RAILWAY_DOMAIN) {
      const url = new URL(window.location.href);
      url.hostname = 'damattsystems.com.br';
      window.location.replace(url.href);
      return null;
    }
  } catch { /* ignore */ }
  return children;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop />
              <CanonicalDomainRedirect>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                {/* Public landing + menu */}
                <Route path="/" element={<AppRoot />} />
                <Route path="/termos" element={<TermosPublic />} />
                <Route path="/privacidade" element={<PrivacidadePublic />} />
                <Route path="/cardapio" element={<Home />} />
                <Route path="/cardapio/:slug" element={<Home />} />
                <Route path="/c/:slug" element={<Home />} />
                <Route path="/cardapio/:slug/pedido/:code" element={<OrderTracking />} />
                <Route path="/cardapio/pedido/:code" element={<OrderTracking />} />
                <Route path="/c/:slug/pedido/:code" element={<OrderTracking />} />
                <Route path="/checkout" element={<Checkout />} />
                {/* URL curta: /milkshakeup (sem /c/) */}
                <Route path="/:slug/checkout" element={<Checkout />} />
                <Route path="/:slug" element={<Home />} />
                <Route path="/:slug/pedido/:code" element={<OrderTracking />} />

                {/* Admin */}
                <Route path="/admin/login" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route
                  path="/admin"
                  element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}
                >
                  <Route index element={<ProtectedRoute permission="dashboard.access"><Dashboard /></ProtectedRoute>} />
                  <Route path="visao-geral" element={<ProtectedRoute permission="overview.access"><VisaoGeral /></ProtectedRoute>} />
                  <Route path="perfil" element={<Perfil />} />
                  <Route path="mesas" element={<ProtectedRoute permission="tables.access" modules={['mesas']}><Mesas /></ProtectedRoute>} />
                  <Route path="mesas/:id" element={<ProtectedRoute permission="tables.access" modules={['mesas']}><MesaDetail /></ProtectedRoute>} />
                  <Route path="comandas" element={<ProtectedRoute permission="comandas.access" modules={['comandas']}><Comandas /></ProtectedRoute>} />
                  <Route path="comandas/:id" element={<ProtectedRoute permission="comandas.access" modules={['comandas']}><ComandaDetail /></ProtectedRoute>} />
                  <Route path="balcao" element={<ProtectedRoute permission="pos.access" modules={['pdv']}><Balcao /></ProtectedRoute>} />
                  <Route path="delivery" element={<ProtectedRoute permission="delivery.access" modules={['delivery']}><DeliveryKanban /></ProtectedRoute>} />
                  <Route path="preparo" element={<ProtectedRoute permission="kds.access" modules={['kds']}><Preparo /></ProtectedRoute>} />
                  <Route path="pedidos" element={<ProtectedRoute permission="orders.access"><Pedidos /></ProtectedRoute>} />
                  <Route path="produtos" element={<ProtectedRoute permission="products.access" modules={['pdv','estoque']}><Produtos /></ProtectedRoute>} />
                  <Route path="produtos/novo" element={<ProtectedRoute permission="products.manage" modules={['pdv','estoque']}><ProdutoForm /></ProtectedRoute>} />
                  <Route path="produtos/:id/editar" element={<ProtectedRoute permission="products.manage" modules={['pdv','estoque']}><ProdutoForm /></ProtectedRoute>} />
                  <Route path="categorias" element={<ProtectedRoute permission="products.access" modules={['pdv','estoque']}><Categorias /></ProtectedRoute>} />
                  <Route path="servicos" element={<ProtectedRoute permission="products.access" modules={['pdv','estoque']}><Servicos /></ProtectedRoute>} />
                  <Route path="estoque" element={<ProtectedRoute permission="stock.access" modules={['estoque']}><Estoque /></ProtectedRoute>} />
                  <Route path="caixa" element={<ProtectedRoute permission="cash.access" modules={['pdv']}><Caixa /></ProtectedRoute>} />
                  <Route path="caixa/movimentacoes" element={<ProtectedRoute permission="cash.access" modules={['pdv']}><MovimentacoesCaixa /></ProtectedRoute>} />
                  <Route path="financeiro/contas" element={<ProtectedRoute permission="financial.access" modules={['financeiro']}><FinanceiroContas /></ProtectedRoute>} />
                  <Route path="financeiro/extrato" element={<ProtectedRoute permission="financial.access" modules={['financeiro']}><FinanceiroExtrato /></ProtectedRoute>} />
                  <Route path="financeiro/pagamentos" element={<ProtectedRoute permission="financial.access" modules={['financeiro']}><FinanceiroFormasPagamento /></ProtectedRoute>} />
                  <Route path="promocoes" element={<ProtectedRoute permission="promotions.access"><Promocoes /></ProtectedRoute>} />
                  <Route path="clientes" element={<ProtectedRoute permission="customers.access"><Clientes /></ProtectedRoute>} />
                  <Route path="relatorios" element={<ProtectedRoute permission="reports.access" modules={['relatorios']}><Relatorios /></ProtectedRoute>} />
                  <Route path="usuarios" element={<ProtectedRoute permission="users.manage"><Usuarios /></ProtectedRoute>} />
                  <Route path="localizacao/enderecos" element={<ProtectedRoute permission="locations.access" modules={['delivery']}><LocalizacaoEnderecos /></ProtectedRoute>} />
                  <Route path="avaliacoes" element={<ProtectedRoute permission="reviews.access" modules={['delivery']}><Avaliacoes /></ProtectedRoute>} />
                  <Route path="configuracoes/gerais" element={<ProtectedRoute permission="settings.access"><ConfiguracoesGerais /></ProtectedRoute>} />
                  <Route path="configuracoes/balanca" element={<ProtectedRoute permission="scale.settings"><ConfiguracoesBalanca /></ProtectedRoute>} />
                  <Route path="configuracoes/impressao" element={<ProtectedRoute permission="printing.settings"><ConfiguracoesImpressao /></ProtectedRoute>} />
                  <Route path="configuracoes/hardware" element={<ProtectedRoute permission="printing.settings"><ConfiguracoesHardware /></ProtectedRoute>} />
                  <Route path="configuracoes/setores-impressao" element={<ProtectedRoute permission="printing.settings" modules={['kds']}><SetoresImpressao /></ProtectedRoute>} />
                  <Route path="configuracoes/cardapio" element={<ProtectedRoute permission="menu.settings" modules={['delivery','comandas']}><CardapioConfig /></ProtectedRoute>} />
                  <Route path="auditoria/cancelamentos" element={<ProtectedRoute permission="audit.access"><Auditoria /></ProtectedRoute>} />
                  <Route path="qrcode" element={<ProtectedRoute permission="qrcode.access" modules={['delivery','comandas']}><QRCodePage /></ProtectedRoute>} />
                  <Route path="tenants" element={<Navigate to="/admin/superadmin/lojas" replace />} />
                  <Route path="sobre" element={<Sobre />} />
                  <Route path="termos" element={<Termos />} />
                  <Route path="privacidade" element={<Privacidade />} />
                </Route>
                <Route
                  path="/admin/superadmin"
                  element={<ProtectedRoute roles={['superadmin']}><SuperAdminLayout /></ProtectedRoute>}
                >
                  <Route index element={<SuperAdminDashboardV2 />} />
                  <Route path="lojas" element={<SuperAdminLojas />} />
                  <Route path="planos" element={<SuperAdminPlanos />} />
                  <Route path="financeiro" element={<SuperAdminFinanceiro />} />
                  <Route path="usuarios" element={<SuperAdminUsuarios />} />
                  <Route path="logs" element={<SuperAdminAudit />} />
                  <Route path="dominios" element={<SuperAdminDomains />} />
                  <Route path="slugs" element={<SuperAdminSlugsV2 />} />
                  <Route path="retencao" element={<SuperAdminRetencao />} />
                  <Route path="saude" element={<SuperAdminSaude />} />
                  <Route path="agentes" element={<SuperAdminAgentes />} />
                </Route>
                </Routes>
              </Suspense>
              </CanonicalDomainRedirect>
              <Toaster />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

serviceWorkerRegistration.register({
  onUpdate: (registration) => window.dispatchEvent(new CustomEvent('sw-update-available', { detail: registration })),
});

export default App;
